import { useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import AttachmentTile from '../Pages/Chats/Chat/ChatSender/AttachmentTile';
import CameraCapture from './CameraCapture';

/**
 * Base AttachmentMenu component - handles modal, camera capture, and file input
 * 
 * @param {Array} buttons - Array of button configs: { icon, label, onClick }
 * @param {Function} onClose - Callback when menu closes
 * @param {Function} onFileSelect - Callback when file is selected (files, type, imageData)
 * @param {boolean} acceptMultiple - Whether to accept multiple files (default: false)
 * @param {Object} ref - Forwarded ref to expose triggerFileInput and triggerCamera methods
 * @param {ReactNode} children - Additional components to render in the grid (e.g., calendar buttons)
 */
const AttachmentMenu = forwardRef(({ buttons, onClose, onFileSelect, acceptMultiple = false, children }, ref) => {
  const imageInputRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    triggerFileInput: () => {
      imageInputRef.current?.click();
    },
    triggerCamera: () => {
      setCameraOpen(true);
    }
  }));

  const handleCameraCapture = useCallback((file) => {
    setCameraOpen(false);
    
    // Create preview URL from file blob
    const previewUrl = URL.createObjectURL(file);
    
    // Create imageData with metadata (no upload yet)
    const imageData = {
      source: 'camera',
      previewUrl
    };

    // Pass the file and imageData to parent
    onFileSelect?.([file], 'camera', imageData);
    onClose();
  }, [onFileSelect, onClose]);

  const handleImageSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onFileSelect?.(files, 'image');
    }
    onClose();
  };

  return (
    <>
      {/* Camera capture modal - shows over everything else */}
      {cameraOpen && (
        <div className="fixed inset-0 z-[200]">
          <CameraCapture
            aspectW={1}
            aspectH={1}
            outputWidth={800}
            onCapture={handleCameraCapture}
            onCancel={() => setCameraOpen(false)}
          />
        </div>
      )}
      
      {/* Attachment menu */}
      <div className="fixed inset-0 z-[100] flex items-center justify-center pt-0 pb-32" onClick={onClose}>
        <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" />
        <div 
          onClick={(e) => e.stopPropagation()} 
          className="relative mx-4 w-full max-w-md rounded-3xl bg-card shadow-2xl p-6"
        >
          <input
            type="file"
            ref={imageInputRef}
            onChange={handleImageSelect}
            accept="image/*"
            multiple={acceptMultiple}
            className="hidden"
          />
          
          <div className="grid grid-cols-2 gap-4">
            {buttons.map((button, index) => (
              <AttachmentTile
                key={index}
                icon={button.icon}
                label={button.label}
                onClick={button.onClick}
              />
            ))}
            {children}
          </div>
        </div>
      </div>
    </>
  );
});

AttachmentMenu.displayName = 'AttachmentMenu';

export default AttachmentMenu;
